import { PUBLISHED_HELP_SAFE_SPANS } from './help-chat-published-spans.generated';

export const SENSITIVE_HELP_CHAT_ERROR =
  "Please don't include personal or health details, such as names, contact details, identifiers or addresses.";

export const SENSITIVE_HELP_TICKET_ERROR =
  'Please remove names, identifiers, clinical details, or incident narratives from support tickets. Use workflow or record IDs instead of resident or client names.';

export const HELP_CHAT_RATE_LIMIT_REPLY =
  "You've sent too many messages. Please wait a moment and try again.";

export const HELP_CHAT_TRANSPORT_ERROR_REPLY =
  "Sorry, I'm having trouble responding right now. Please try again or browse Help Centre.";

const HELP_PHI_PATTERNS_BASE = [
  /\b(?:medicare|crn)\b[^\d\n]{0,24}(?:\d[\s-]*){10,12}\b/i,
  /\b(?:ihi|healthcare identifier)\b[^\d\n]{0,24}(?:\d[\s-]*){16}\b/i,
  /\b(?:Mr|Mrs|Ms|Miss|Dr|mr|mrs|ms|miss|dr)\.?\s+[A-Z][a-z]+\b/,
  /\b[A-Z][a-z]{2,}\s+(had a fall|fell)\b/,
  /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i,
  /(?:\+61[\s-]?[2-478]|\b0[2-478])(?:[\s-]?\d){8}\b/i,
  /\(\s*0[2-478]\s*\)\s*(?:\d[\s-]*){8}\b/i,
  /\bdva(?:\s+(?:number|no\.?|file\s+number|id))?\s*[:#-]?\s*[A-Z]{1,3}\d[A-Z0-9 -]{4,}\b/i,
  /\bndis(?:\s+(?:number|no\.?|participant(?:\s+number)?|id))?\s*[:#-]?\s*(?:\d[ -]*){6,}\b/i,
  /\bcard\s*[:#-]?\s*(?:\d[ -]*){10}\b/i,
  /\b\d{1,5}\s+[A-Z][A-Za-z'-]*(?:\s+[A-Z][A-Za-z'-]*){0,3}\s+(?:street|st|road|rd|avenue|ave|drive|dr|court|ct|lane|ln|place|pl|way|crescent|cres|parade|pde|terrace|tce|boulevard|blvd|close|cl|circuit|cct)\b/i,
  /\b(?:diagnosed with|allergic to|clinical diagnosis(?: is)?)\s+[A-Za-z][A-Za-z -]{2,}\b/i,
  /\b(?:blood pressure|bp|heart rate|pulse|temperature|temp|oxygen saturation|spo2|blood glucose)\s*(?:is|was|of|:)?\s*\d{2,3}(?:\s*\/\s*\d{2,3}|(?:\.\d+)?\s*%?)?\b/i,
  /\b(?:wanders?|wandering)\s+(?:at night|overnight)\b/i,
] as const;

const CLINICAL_DIAGNOSIS_PATTERN =
  /\b([A-Za-z][A-Za-z'’-]{2,})\s+(?:has|had|with)\s+(?:dementia|diabetes|epilepsy|cancer|parkinson(?:'s)?|a stroke|seizures?)\b/gi;
const MEDICINE_DETAIL_PATTERN =
  /\b(takes|taking|prescribed|on|medication(?:\s+is|:))\s+(warfarin|aspirin|paracetamol|sertraline|insulin|digoxin|metformin|morphine|oxycodone|fentanyl|[A-Za-z]{3,}(?:cillin|pril|sartan|statin|olol))\b/gi;
const GENERIC_COHORT_NOUNS = new Set([
  'clients',
  'employees',
  'participants',
  'patients',
  'residents',
  'staff',
  'workers',
]);

const DOB_MONTH =
  '(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)';
const DOB_SEPARATOR = '(?:\\s*(?:[./,-]|\\bof\\b)\\s*|\\s+)';
const DOB_VALUE_PATTERN = new RegExp(
  [
    '\\b\\d{6,8}\\b',
    `\\b\\d{4}${DOB_SEPARATOR}\\d{1,2}${DOB_SEPARATOR}\\d{1,2}\\b`,
    `\\b\\d{1,2}${DOB_SEPARATOR}\\d{1,2}${DOB_SEPARATOR}\\d{2,4}\\b`,
    `\\b\\d{1,2}(?:st|nd|rd|th)?${DOB_SEPARATOR}${DOB_MONTH}\\.?${DOB_SEPARATOR}\\d{2,4}\\b`,
    `\\b${DOB_MONTH}\\.?${DOB_SEPARATOR}\\d{1,2}(?:st|nd|rd|th)?${DOB_SEPARATOR}\\d{2,4}\\b`,
    '\\b\\d{4}' +
      DOB_SEPARATOR +
      DOB_MONTH +
      '\\.?' +
      DOB_SEPARATOR +
      '\\d{1,2}(?:st|nd|rd|th)?\\b',
  ].join('|'),
  'i',
);

const HELP_CHAT_ONLY_PHI_PATTERNS = [/\b\d{8,}\b/] as const;

const PERSON_ROLE_NAME = new RegExp(
  "\\b([Cc]lient|[Rr]esident|[Pp]articipant|[Pp]atient|[Ss]taff member|[Ss]taff worker|[Ww]orker|[Ee]mployee)\\s+(?:(named|called)\\s+)?([A-Z][A-Za-z'’.-]{2,})(?:\\s+([A-Z][A-Za-z'’.-]{2,}))?",
  'g',
);
const EXPLICIT_NAMED_PERSON = new RegExp(
  "\\b(?:client|resident|participant|patient|staff member|staff worker|worker|employee)\\s+(?:named|called)\\s+([A-Za-z][A-Za-z'’.-]{1,30})(?:\\s+([A-Za-z][A-Za-z'’.-]{1,30}))?",
  'i',
);

const PERSON_NAME_TOKEN = "[A-Za-z][A-Za-z'’.-]{1,30}";
const CAPITAL_PERSON_NAME_PAIR = new RegExp(
  "\\b([A-Z][A-Za-z'’.-]{1,30}\\s+[A-Z][A-Za-z'’.-]{1,30})\\b",
  'g',
);
const LOWERCASE_PERSON_NAME_IN_FAILURE = new RegExp(
  `\\b(?:can(?:not|'t|’t)|could(?:\\s+not|n't|n’t))\\s+(${PERSON_NAME_TOKEN}\\s+${PERSON_NAME_TOKEN})\\s+(?:clock\\s+in|log\\s+in|open|access|use|save|sync|load)\\b`,
  'gi',
);
const PRODUCT_OR_FUNCTION_WORDS = new Set([
  'a',
  'an',
  'app',
  'button',
  'draft',
  'form',
  'my',
  'page',
  'portal',
  'roster',
  'save',
  'screen',
  'that',
  'the',
  'this',
  'your',
]);
const GENERIC_LOWERCASE_SUBJECT_MODIFIERS = new Set(['care', 'new', 'our']);

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const PUBLISHED_HELP_CONTROL_PATTERN = new RegExp(
  `\\b(?:${[...PUBLISHED_HELP_SAFE_SPANS]
    .sort((left, right) => right.length - left.length)
    .map(escapeRegExp)
    .join('|')})\\b`,
  'gi',
);

type PublishedControlSpan = { start: number; end: number };

function publishedHelpControlSpans(text: string): PublishedControlSpan[] {
  return [...text.matchAll(PUBLISHED_HELP_CONTROL_PATTERN)].map((match) => ({
    start: match.index ?? 0,
    end: (match.index ?? 0) + match[0].length,
  }));
}

/** Mask only reviewed published product labels; raw value checks still use original text. */
export function maskPublishedHelpControls(text: string): string {
  return text.replace(PUBLISHED_HELP_CONTROL_PATTERN, (match) => {
    // Controls such as New Client end in a person role. Preserve that terminal
    // subject so following observations cannot disappear with the UI label.
    const role = match.match(
      /\b(?:client|resident|worker|patient|participant|person|care recipient)\s*$/i,
    )?.[0];
    return role ? ' '.repeat(match.length - role.length) + role : ' '.repeat(match.length);
  });
}

function isWithinPublishedControl(
  start: number,
  end: number,
  spans: readonly PublishedControlSpan[],
): boolean {
  return spans.some((span) => start >= span.start && end <= span.end);
}

function containsPublishedLabelWithPersonValue(
  text: string,
  spans: readonly PublishedControlSpan[],
): boolean {
  for (const span of spans) {
    const suppliedValue = text.slice(span.end);
    const safeStatus =
      /^(?:blank|empty|failed|hidden|limited|missing|unavailable|unknown|not\s+(?:loading|visible|working))\b/i;
    const labelledValue = suppliedValue.match(/^\s*[:=]\s*([A-Z][a-z'’-]{2,})\b/);
    if (labelledValue) {
      if (!safeStatus.test(labelledValue[1]!)) return true;
      continue;
    }
    const publishedLabel = text.slice(span.start, span.end);
    if (!/\b(?:client|resident|participant|patient|worker|employee|profile)$/i.test(publishedLabel))
      continue;
    const adjacentValue = suppliedValue.match(/^\s+([A-Z][a-z'’-]{2,})\b(.*)$/);
    if (!adjacentValue || safeStatus.test(adjacentValue[1]!)) continue;
    if (
      /^\s*(?:$|[.!?,]|(?:can(?:not|'t|’t)|could(?:\s+not|n't|n’t)|does(?:\s+not|n't|n’t)|did(?:\s+not|n't|n’t)|won(?:'t|’t)|has(?:\s+not|n't|n’t)|is(?:\s+not|n't|n’t)|was(?:\s+not|n't|n’t)|needs?|failed|fell|locked)\b)/i.test(
        adjacentValue[2]!,
      )
    )
      return true;
  }
  return false;
}

function textMatchesAnyPattern(text: string, patterns: readonly RegExp[]): boolean {
  return patterns.some((pattern) => pattern.test(text));
}

function containsRolePlusPersonName(text: string, spans: readonly PublishedControlSpan[]): boolean {
  const explicit = EXPLICIT_NAMED_PERSON.exec(text);
  if (
    explicit &&
    !isWithinPublishedControl(explicit.index, explicit.index + explicit[0].length, spans)
  )
    return true;
  for (const match of text.matchAll(PERSON_ROLE_NAME)) {
    const explicitName = match[2];
    const first = match[3];
    const second = match[4];
    const start = match.index ?? 0;
    if (!first || isWithinPublishedControl(start, start + match[0].length, spans)) continue;
    if (
      !explicitName &&
      /^(?:profile|overview|onboarding|details|journey|record|list|tab|portal|workstation|csv)$/i.test(
        first,
      ) &&
      !second
    )
      continue;
    if (explicitName || second || first) return true;
  }
  return false;
}

function containsContextualPersonName(
  text: string,
  spans: readonly PublishedControlSpan[],
): boolean {
  for (const match of text.matchAll(LOWERCASE_PERSON_NAME_IN_FAILURE)) {
    const lowercaseFailureName = match[1];
    const parts = lowercaseFailureName?.toLocaleLowerCase().split(/\s+/) ?? [];
    const matchStart = match.index ?? 0;
    const nameStart = matchStart + match[0].toLocaleLowerCase().indexOf(lowercaseFailureName!);
    if (
      parts.length === 2 &&
      !isWithinPublishedControl(nameStart, nameStart + lowercaseFailureName!.length, spans) &&
      !GENERIC_LOWERCASE_SUBJECT_MODIFIERS.has(parts[0]!) &&
      !GENERIC_COHORT_NOUNS.has(parts[1]!) &&
      parts.every((part) => !PRODUCT_OR_FUNCTION_WORDS.has(part))
    ) {
      return true;
    }
  }
  for (const match of text.matchAll(CAPITAL_PERSON_NAME_PAIR)) {
    const [modifier, role] = match[0].toLowerCase().split(/\s+/);
    // A determiner plus an unnamed role (New Client) is grammatical product
    // copy, not a harvested safe span or an exemption for a supplied name.
    if (
      modifier &&
      GENERIC_LOWERCASE_SUBJECT_MODIFIERS.has(modifier) &&
      /^(?:client|resident|worker|participant|patient|employee)$/.test(role ?? '')
    )
      continue;
    const start = match.index ?? 0;
    const end = start + match[0].length;
    if (isWithinPublishedControl(start, end, spans)) continue;
    const before = text.slice(Math.max(0, start - 48), start);
    const after = text.slice(end, end + 72);
    if (/\b(?:for|about|with)\s*$/i.test(before)) return true;
    if (/\b(?:can(?:not|'t|’t)|could(?:\s+not|n't|n’t)|why\s+can(?:not|'t|’t))\s*$/i.test(before))
      return true;
    if (
      /^\s*(?:can(?:not|'t|’t)|could(?:\s+not|n't|n’t)|does(?:\s+not|n't|n’t)|did(?:\s+not|n't|n’t)|won(?:'t|’t)|has(?:\s+not|n't|n’t)|is(?:\s+not|n't|n’t)|was(?:\s+not|n't|n’t)|needs?|failed|fell|locked|(?:is|was)\s+(?:locked|unable|missing|blocked))\b/i.test(
        after,
      )
    )
      return true;
  }
  return false;
}

function containsClinicalDiagnosis(text: string): boolean {
  for (const match of text.matchAll(CLINICAL_DIAGNOSIS_PATTERN)) {
    if (!GENERIC_COHORT_NOUNS.has(match[1]!.toLocaleLowerCase())) return true;
  }
  return false;
}

function containsMedicineDetail(text: string): boolean {
  for (const match of text.matchAll(MEDICINE_DETAIL_PATTERN)) {
    const prefix = match[1]!.toLocaleLowerCase();
    if (prefix === 'on') {
      const before = text.slice(0, match.index ?? 0);
      const cohort = before.match(/\b([A-Za-z]+)\s*$/)?.[1]?.toLocaleLowerCase();
      if (/^\s*(?:how|where|which|can)\b/i.test(text) && cohort && GENERIC_COHORT_NOUNS.has(cohort))
        continue;
    }
    return true;
  }
  return false;
}

/** A generic field label is safe; a nearby value-shaped token is not. */
function containsDateOfBirthValue(text: string): boolean {
  for (const match of text.matchAll(
    /(?:\bdate of birth\b|\bbirthdate\b|\bd\s*\.?\s*o\s*\.?\s*b\s*\.?)(?![a-z])/gi,
  )) {
    const valueStart = (match.index ?? 0) + match[0].length;
    if (DOB_VALUE_PATTERN.test(text.slice(valueStart, valueStart + 96))) return true;
  }
  return false;
}

function containsUnlabelledDateOfBirthValue(text: string): boolean {
  for (const match of text.matchAll(/\bborn\b/gi)) {
    const valueStart = (match.index ?? 0) + match[0].length;
    if (DOB_VALUE_PATTERN.test(text.slice(valueStart, valueStart + 96))) return true;
  }
  return false;
}

function containsSensitiveHelpDetail(text: string, patterns: readonly RegExp[]): boolean {
  const publishedControls = publishedHelpControlSpans(text);
  if (containsPublishedLabelWithPersonValue(text, publishedControls)) return true;
  return (
    containsRolePlusPersonName(text, publishedControls) ||
    containsContextualPersonName(text, publishedControls) ||
    containsDateOfBirthValue(text) ||
    containsUnlabelledDateOfBirthValue(text) ||
    containsClinicalDiagnosis(text) ||
    containsMedicineDetail(text) ||
    textMatchesAnyPattern(text, patterns)
  );
}

export function containsSensitiveHelpChatDetail(text: string): boolean {
  return containsSensitiveHelpDetail(text, [
    ...HELP_PHI_PATTERNS_BASE,
    ...HELP_CHAT_ONLY_PHI_PATTERNS,
  ]);
}

export function containsSensitiveHelpTicketDetail(text: string): boolean {
  return containsSensitiveHelpDetail(text, HELP_PHI_PATTERNS_BASE);
}

export function supportTicketTextContainsSensitiveDetail(
  ...parts: Array<string | null | undefined>
): boolean {
  return parts
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .some((part) => containsSensitiveHelpTicketDetail(part));
}
